import { Vector } from './vector.ts';

import { emptyInput } from './entities.ts';
import type {
  Bullet,
  ControlPoint,
  Entity,
  EntityId,
  Player,
  Team,
} from './entities.ts';
import { random } from './math.ts';
import {
  CAPTURE_THRESHOLD,
  CONTROL_POINT_SIZE,
  CONTROL_POINT_SPACING,
  DASH_COOLDOWN,
  DASH_DURATION,
  DASH_SPEED,
  KILL_SIZE_INCREASE,
  MAP_SIZE,
  MS_PER_SECOND,
  PLAYER_SIZE,
  PLAYER_SPEED,
} from './parameters.ts';
import { WEAPONS } from './weapons.ts';
import type { WeaponName } from './weapons.ts';

/**
 * The game, and the only mutable thing here. Both ends run it: the server owns
 * the truth, the client runs the same functions between two snapshots so the
 * picture keeps moving.
 */
export interface World {
  entities: Map<EntityId, Entity>;
  frame: number;
}

export function createWorld(): World {
  return { entities: new Map(), frame: 0 };
}

/**
 * A death, as the world tells it. Nothing here knows about sockets or scores,
 * so `stepWorld` hands the ids out and whoever keeps the count translates them.
 */
export interface Kill {
  killerId: EntityId;
  victimId: EntityId;
}

/** The arena's furniture. Only the server places it; clients are told. */
export function addControlPoints(world: World) {
  for (
    let x = CONTROL_POINT_SPACING;
    x < MAP_SIZE;
    x += CONTROL_POINT_SPACING
  ) {
    for (
      let y = CONTROL_POINT_SPACING;
      y < MAP_SIZE;
      y += CONTROL_POINT_SPACING
    ) {
      addEntity(world, createControlPoint(Vector.of(x, y)));
    }
  }
}

export function addEntity<T extends Entity>(world: World, entity: T): T {
  world.entities.set(entity.id, entity);
  return entity;
}

export function removeEntity(world: World, id: EntityId) {
  world.entities.delete(id);
}

export function getPlayer(world: World, id: EntityId) {
  const entity = world.entities.get(id);
  return entity?.kind === 'player' ? entity : undefined;
}

export function createPlayer(props: {
  id: EntityId;
  name: string;
  team: Team;
  weapon: WeaponName;
  position: Vector;
}): Player {
  return {
    kind: 'player',
    id: props.id,
    name: `${WEAPONS[props.weapon].playerTitle} ${props.name}`,
    team: props.team,
    position: props.position,
    size: PLAYER_SIZE,
    aim: Vector.of(1, 0),
    weapon: props.weapon,
    clip: WEAPONS[props.weapon].clipSize,
    cooldown: 0,
    reload: 0,
    dash: 0,
    dashCooldown: 0,
    input: emptyInput(),
  };
}

function createControlPoint(position: Vector): ControlPoint {
  return {
    kind: 'control-point',
    id: nextId('c'),
    position,
    size: CONTROL_POINT_SIZE,
    ownerTeam: null,
    capturingTeam: null,
    capture: 0,
  };
}

/**
 * One server frame: everything moves, shoots, dies and is buried. What it
 * returns is the frame's deaths, because a removed entity is the only trace
 * they leave and a scoreboard needs to hear about them once.
 */
export function stepWorld(world: World, delta: number): Kill[] {
  world.frame++;

  const kills: Kill[] = [];

  for (const entity of [...world.entities.values()]) {
    if (entity.kind === 'player') stepPlayer(world, entity, delta);
  }

  // Where every bullet stands before it moves. What it hits is the line it
  // sweeps this frame, and that line only exists between the two.
  const from = new Map<EntityId, Vector>();

  for (const entity of world.entities.values()) {
    if (entity.kind === 'bullet') from.set(entity.id, entity.position);
  }

  moveBullets(world, delta);

  for (const entity of [...world.entities.values()]) {
    if (entity.kind !== 'bullet') continue;

    resolveBulletHits(
      world,
      entity,
      from.get(entity.id) ?? entity.position,
      kills,
    );

    // Only now: a bullet that ran out of road on this stride still travelled
    // it, and burying it first is how a shot dies against a wall it crossed
    // somebody in front of.
    if (!world.entities.has(entity.id)) continue;

    if (entity.range <= 0 || isOutOfMap(entity.position)) {
      removeEntity(world, entity.id);
    }
  }

  return kills;
}

/** Movement, reload and the trigger. The client runs `movePlayer` instead. */
export function stepPlayer(world: World, player: Player, delta: number) {
  movePlayer(player, delta);

  if (player.reload > 0) return;

  if (player.clip === 0) {
    player.reload = WEAPONS[player.weapon].reloadTime;
    return;
  }

  if (player.input.shoot && player.cooldown === 0) fire(world, player);
}

/**
 * What the client repeats for its own player between snapshots: the same
 * arithmetic on the same input, so its prediction lands where the server does.
 */
export function movePlayer(player: Player, delta: number) {
  const { input } = player;

  // The shove is a duration, not a number of frames: a frame that ends in the
  // middle of one covers part of its ground at each speed. Without the split a
  // 30 Hz end dashes a body further than a 60 Hz one, every single time.
  const shoved = Math.min(player.dash, delta);
  const step =
    (PLAYER_SPEED * (delta + shoved * (DASH_SPEED - 1))) / MS_PER_SECOND;

  let { x, y } = player.position;

  player.aim = Vector.of(input.aim.x, input.aim.y);

  if (input.up) y -= step;
  if (input.down) y += step;
  if (input.left) x -= step;
  if (input.right) x += step;

  // A dash with no key held goes where the player is looking: standing still
  // and pressing space has to mean something.
  if (player.dash > 0 && x === player.position.x && y === player.position.y) {
    const push = player.aim.setMagnitude(step);
    x += push.x;
    y += push.y;
  }

  player.position = Vector.of(clamp(x), clamp(y));
  player.cooldown = Math.max(0, player.cooldown - delta);
  player.dash = Math.max(0, player.dash - delta);
  player.dashCooldown = Math.max(0, player.dashCooldown - delta);

  if (player.reload > 0) {
    player.reload = Math.max(0, player.reload - delta);
    if (player.reload === 0) player.clip = WEAPONS[player.weapon].clipSize;
  }
}

/**
 * Reloading before the clip runs dry. Nothing new is drawn for it: `reload`
 * already travels in the snapshot and the renderer already paints its arc.
 */
export function reloadWeapon(player: Player) {
  if (player.reload > 0) return;
  if (player.clip === WEAPONS[player.weapon].clipSize) return;

  player.reload = WEAPONS[player.weapon].reloadTime;
}

/**
 * The shove. Both ends run this off their own copy — same rule, same data — so
 * they only disagree if the two cooldowns land on different sides of the same
 * millisecond. Waiting for the server instead would leave the prediction a
 * hundred pixels out, under the tolerance at any ping: never snapped back.
 */
export function startDash(player: Player) {
  if (player.dashCooldown > 0) return;

  player.dash = DASH_DURATION;
  player.dashCooldown = DASH_COOLDOWN;
}

/**
 * Bullets fly in a straight line, so both ends move them the same way — which
 * is what lets the server leave their positions out of most snapshots. Only
 * movement: who dies is the server's word, and it arrives as a removal.
 */
export function moveBullets(world: World, delta: number) {
  for (const entity of world.entities.values()) {
    if (entity.kind !== 'bullet') continue;

    const travelled = entity.velocity.scale(delta / MS_PER_SECOND);
    entity.position = entity.position.add(travelled);
    entity.range -= travelled.magnitude;
  }
}

function resolveBulletHits(
  world: World,
  bullet: Bullet,
  from: Vector,
  kills: Kill[],
) {
  if (!world.entities.has(bullet.id)) return;

  const target = firstInTheWay(world, bullet, from);

  if (!target) return;

  if (target.kind === 'player') kills.push(killPlayer(world, target, bullet));
  else takeCaptureHit(target, bullet.team);

  removeEntity(world, bullet.id);
}

/**
 * What the bullet ran into on the way, which is not the same as what it ended
 * up on top of. A sniper round covers 17 px in a frame and a player is 13 px
 * wide to it, so a graze used to have a window shorter than the stride: the
 * shot went visibly through somebody and came out the far side alive. A long
 * frame — the loop hitching — did the same to a shot through the middle.
 *
 * Nearest first, because a stride can cross two people and only the first of
 * them was ever hit.
 */
function firstInTheWay(world: World, bullet: Bullet, from: Vector) {
  let closest: Player | ControlPoint | undefined;
  let reach = Infinity;

  for (const entity of world.entities.values()) {
    if (entity.kind === 'bullet' || entity.id === bullet.ownerId) continue;

    const miss = distanceToSegment(entity.position, from, bullet.position);
    if (miss >= bullet.size + entity.size) continue;

    const distance = from.distance(entity.position);

    if (distance < reach) {
      reach = distance;
      closest = entity;
    }
  }

  return closest;
}

/** How far a point lies off a line that starts and ends somewhere. */
function distanceToSegment(point: Vector, from: Vector, to: Vector) {
  const line = to.sub(from);
  const length = line.magnitude;

  if (length === 0) return point.distance(from);

  const along = point.sub(from);
  const travelled = (along.x * line.x + along.y * line.y) / (length * length);
  const nearest = Math.min(1, Math.max(0, travelled));

  return point.distance(from.add(line.scale(nearest)));
}

/**
 * The killer is named by the bullet, not by the world: it may have died since
 * it fired, and whoever counts will simply find nobody to credit.
 */
function killPlayer(world: World, victim: Player, bullet: Bullet): Kill {
  const killer = getPlayer(world, bullet.ownerId);
  if (killer) killer.size += victim.size * KILL_SIZE_INCREASE;
  removeEntity(world, victim.id);
  return { killerId: bullet.ownerId, victimId: victim.id };
}

/**
 * A control point flips after `CAPTURE_THRESHOLD` hits from one team; a hit
 * from anybody else walks that count back. The owner shooting it repairs it.
 */
function takeCaptureHit(point: ControlPoint, team: Team) {
  if (point.ownerTeam === team) {
    point.capture = Math.max(0, point.capture - 1);
    if (point.capture === 0) point.capturingTeam = null;
    return;
  }

  if (point.capturingTeam !== team) {
    if (point.capture > 0) {
      point.capture--;
      return;
    }

    point.capturingTeam = team;
  }

  point.capture++;

  if (point.capture >= CAPTURE_THRESHOLD) {
    point.ownerTeam = team;
    point.capturingTeam = null;
    point.capture = 0;
  }
}

function fire(world: World, player: Player) {
  const weapon = WEAPONS[player.weapon];
  const aim = player.aim.isZero ? Vector.of(1, 0) : player.aim;

  for (let i = 0; i < weapon.burst; i++) {
    const radians = aim.radians + deviate(weapon.deviation, weapon.reliability);

    addEntity(world, {
      kind: 'bullet',
      id: nextId('b'),
      position: player.position,
      size: Math.max(2, Math.floor(player.size / 3)),
      team: player.team,
      ownerId: player.id,
      velocity: Vector.fromRadians(radians, weapon.bulletSpeed),
      range: weapon.range,
    });
  }

  player.clip--;
  player.cooldown = weapon.burstDelay;
}

/** The first reliability roll that passes narrows the spread; only one can. */
function deviate(deviation: number, reliability: number[]) {
  for (let i = 0; i < reliability.length; i++) {
    if (Math.random() <= reliability[i]) {
      deviation /= i + 2;
      break;
    }
  }

  return random(-deviation, deviation);
}

function isOutOfMap({ x, y }: Vector) {
  return x < 0 || y < 0 || x > MAP_SIZE || y > MAP_SIZE;
}

function clamp(value: number) {
  return Math.min(MAP_SIZE, Math.max(0, value));
}

let nextEntityNumber = 0;

/** Ids travel as object keys 30 times a second, so they are kept short. */
export function nextId(prefix: string): EntityId {
  return `${prefix}${nextEntityNumber++}`;
}
